import { useEffect, useRef, useState } from "react";
import BaseTextField from "../../../components/ui/BaseTextField";
import BaseAutoSelect from "../../../components/ui/BaseAutoSelect";
import { useMutation } from "@tanstack/react-query";
import BaseButton from "../../../components/ui/BaseButton";
import * as yup from "yup";
import { useFormik } from "formik";
import { createOrUpdateUserAddress, updateUserDetails } from "../../../services/api/user";
import { useDispatch } from "react-redux";
import { showToast } from "../../../store/toast/toastSlice";
import BaseLoader from "../../../components/ui/BaseLoader";
import { deleteDigitalAsset, uploadIdDocuments } from "../../../services/api/digitalAsset";
import SearchLocation from "../../../components/SearchLocation/SearchLocation";
import { useCurrentUser } from "../../../hooks/useCurrentUser";
import { useCountries } from "../../../hooks/useCountries";
import { isIdentityLocked } from "../../../utils/helpers";

const validationSchema = yup.object({
  addressLine1: yup.string("Enter your address").required("Address Line 1 is required"),
  addressLine2: yup.string("Enter your address").notRequired(),
  country: yup.string("Enter your country name").required("Country is required"),
  city: yup.string("Enter your city name").required("City is required"),
  state: yup.string("Enter your state").required("State is required"),
  zipCode: yup.string("Enter your Zip Code").notRequired(),
});

const VerifyIdentity = ({
  address,
  isUserLoading,
  nationality,
  refetchMe,
  identityDocuments,
  mode,
  setIsAllowToSubmit,
  userBillingRates,
  onClose,
  onDirtyChange,
}) => {
  const { countries, getCountryLabel } = useCountries();
  const { userData } = useCurrentUser();
  // Nationality and ID documents are frozen once the profile is approved.
  const identityLocked = isIdentityLocked(userData);
  const fileInputRef = useRef(null);
  const dispatch = useDispatch();
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [newFiles, setNewFiles] = useState([]);
  const [showBillingRatesAlert, setShowBillingRatesAlert] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  const {
    isPending: isUploadingIdentityLoading,
    error: isUploadingIdentityError,
    data: uploadedIdentityData,
    mutateAsync: uploadIdDocumentsMutation,
  } = useMutation({
    mutationFn: uploadIdDocuments,
    enabled: false,
  });

  const {
    isPending: isDeletedDigitalAssetApiLoading,
    error: isDeletedDigitalAssetApiError,
    data: deletedDigitalAsset,
    mutateAsync: deleteDigitalAssetMutation,
  } = useMutation({
    mutationFn: deleteDigitalAsset,
    enabled: false,
  });

  const {
    isPending: isUserAddressLoading,
    error: isUserAddressError,
    data: userAddressData,
    mutateAsync: createOrUpdateUserAddressMutation,
  } = useMutation({
    mutationFn: createOrUpdateUserAddress,
    enabled: false,
  });

  const {
    isPending: isUserUpdateApiLoading,
    error: isUserUpdateApiError,
    data: userDataUpdate,
    mutateAsync: updateUserDetailsMutation,
  } = useMutation({
    mutationFn: updateUserDetails,
    enabled: false,
  });

  const formik = useFormik({
    initialValues: {
      addressLine1: "",
      addressLine2: "",
      country: "",
      city: "",
      state: "",
      zipCode: "",
      timeZone: "",
      latitude: "",
      longitude: "",
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      handleSubmit(values);
    },
    validateOnChange: true,
    validateOnBlur: true,
    enableReinitialize: true,
  });
  const normalizeAddress = (a) => {
    const addr = a || {};
    return {
      addressLine1: addr.addressLine1 || "",
      addressLine2: addr.addressLine2 || "",
      country: addr.country || "",
      city: addr.city || "",
      state: addr.state || "",
      zipCode: addr.zipCode || "",
      timeZone: addr.timeZone || "",
      latitude: addr.latitude || "",
      longitude: addr.longitude || "",
    };
  };

  const initialAddressRef = useRef(normalizeAddress(address));
  const hasUserData =
    address?.addressLine1 ||
    address?.addressLine2 ||
    address?.city ||
    address?.state ||
    address?.country ||
    address?.zipCode ||
    nationality ||
    (identityDocuments && identityDocuments.length > 0);
  useEffect(() => {
    if (hasUserData) {
      setIsEditMode(false);
    } else {
      setIsEditMode(true);
    }
  }, [address, nationality, identityDocuments]);
  useEffect(() => {
    initialAddressRef.current = {
      ...normalizeAddress(address),
      nationality: userData?.nationality || "",
    };
  }, [address, userData]);

  useEffect(() => {
    setSelectedFiles([]);
    setNewFiles([]);
  }, [userData?.id]);

  useEffect(() => {
    initialAddressRef.current = normalizeAddress(address);
  }, [address]);

  useEffect(() => {
    if (mode !== "USER_SETTINGS") {
      if (!address?.country) return;

      const prevCountry = initialAddressRef.current.country;
      if (prevCountry && prevCountry !== formik.values.country) {
        userBillingRates?.forEach((rate) => {
          rate.amount = 0;
        });
      }
    }
  }, [formik.values.country]);

  const hasAddressChanged = () => {
    const curr = normalizeAddress(formik.values);
    const init = initialAddressRef.current;
    return Object.keys(curr).some((k) => String(curr[k]) !== String(init[k]));
  };

  useEffect(() => {
    if (address) {
      formik.setValues({
        addressLine1: address.addressLine1 || "",
        addressLine2: address.addressLine2 || "",
        country: address.country || "",
        city: address.city || "",
        state: address.state || "",
        zipCode: address.zipCode || "",
        timeZone: address.timeZone || "",
        latitude: address.latitude || "",
        longitude: address.longitude || "",
        nationality: userData?.nationality,
      });
    }
  }, [address]);
  useEffect(() => {
    if (mode !== "USER_SETTINGS") {
      if (
        formik?.values?.nationality &&
        identityDocuments?.length > 0 &&
        userBillingRates &&
        userBillingRates.length > 0 &&
        userBillingRates.every((rate) => rate.rate > 0)
      ) {
        setIsAllowToSubmit(true);
      } else {
        setIsAllowToSubmit(false);
      }
    }
  }, [formik?.values?.nationality, identityDocuments]);

  const handleSubmit = async (e) => {
    const errors = await formik.validateForm();
    const isValid = Object.keys(errors).length === 0;
    const addressChanged = hasAddressChanged();
    const payload = {
      ...formik.values,
      timeZone: formik.values.timeZone || address?.timeZone || "",
    };
    if (addressChanged && !isValid) {
      return;
    }
    let keepOpenForBillingAlert = false;
    try {
      if (addressChanged) {
        await createOrUpdateUserAddressMutation(payload);
        dispatch(
          showToast({
            message: "Address updated successfully",
            type: "success",
          }),
        );
        if (mode !== "USER_SETTINGS" && address?.country) {
          const prevCountry = initialAddressRef.current.country;
          if (prevCountry && prevCountry !== formik.values.country) {
            if (userBillingRates?.every((rate) => rate.rate > 0)) {
              userBillingRates.forEach((rate) => {
                rate.amount = 0;
              });
              setShowBillingRatesAlert(true);
              keepOpenForBillingAlert = true;
            }
          }
        }
      }
      if (newFiles.length > 0) {
        await handleFileUploading(newFiles);
      }

      await refetchMe();
      setSelectedFiles([]);
      setNewFiles([]);
      // Save completed successfully — close the modal.
      if (onClose && !keepOpenForBillingAlert) onClose();
    } catch (error) {
      console.error("Submission failed:", error);
      dispatch(
        showToast({
          message: "Something went wrong. Please try again.",
          type: "error",
        }),
      );
    }
  };

  const handleButtonClick = () => {
    fileInputRef.current.click();
  };

  const handleFileChange = async (event) => {
    const files = Array.from(event.target.files); // ✅ Convert to array of file objects
    if (!files.length) return; // ✅ Prevent empty uploads

    setSelectedFiles((prevFiles) => [
      ...files.map((file) => ({
        name: file.name || "Unknown File",
        url: URL.createObjectURL(file), // ✅ Temporary URL for preview
      })),
    ]);
    setNewFiles(files);
  };

  // Remove a selected (not-yet-saved) file so the user can re-choose before Save.
  const handleRemoveSelectedFile = (index) => {
    const removed = selectedFiles[index];
    if (removed?.url) URL.revokeObjectURL(removed.url);
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
    setNewFiles((prev) => prev.filter((_, i) => i !== index));
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleFileUploading = async (files) => {
    try {
      const formData = new FormData();
      files.forEach((file) => formData.append("documents", file)); // ✅ Append files to FormData

      // ✅ Ensure `uploadIdDocumentsMutation` supports FormData
      const identityResponse = await uploadIdDocumentsMutation(formData);

      dispatch(
        showToast({
          message: "Identity updated successfully",
          type: "success",
        }),
      );

      files.forEach((file) => URL.revokeObjectURL(file.url));

      refetchMe();
      setSelectedFiles([]);
      setNewFiles([]);
    } catch (error) {
      console.error("Upload failed:", error);
      if (error?.status === 400) {
        dispatch(
          showToast({
            message: "Identity update failed. PDF or Image allowed.",
            type: "error",
          }),
        );
        if (error) error.handled = true;
      }
      // Re-throw so handleSubmit knows the save did not complete and keeps the modal open.
      throw error;
    }
  };

  /* const formattedCitiesData = cities?.data?.map((city) => {
    return {
      label: city,
      value: city,
    };
  }); */
  const handlePlaceSelected = (selectedAddress) => {
    const updatedValues = {
      ...formik.values,
      addressLine1: selectedAddress.completeAddress || "",
      city: selectedAddress.city || "",
      country: selectedAddress.country || "",
      state: selectedAddress.state || "",
      zipCode: selectedAddress.zipCode || "",
      timeZone: selectedAddress.timeZoneName || "",
      latitude: selectedAddress.latitude || "",
      longitude: selectedAddress.longitude || "",
    };
    formik.setValues(updatedValues);
    if (!selectedAddress.city) {
      formik.setFieldTouched("city", true, false);
    }
    if (!selectedAddress.state) {
      formik.setFieldTouched("state", true, false);
    }
  };

  const handleChangeNationality = async (e) => {
    formik.setFieldValue("nationality", e.target.value);
    await updateUserDetailsMutation({
      nationality: e.target.value,
    });
  };
  const handleCancel = () => {
    formik.setValues({
      ...initialAddressRef.current,
      nationality: userData?.nationality || "",
    });

    setSelectedFiles([]);
    setNewFiles([]);

    setShowBillingRatesAlert(false);
    setIsEditMode(false);
  };

  // A submitted profile is a record of what was sent, so nothing here reopens until
  // an admin acts on it.
  const isReadOnly = mode !== "USER_SETTINGS" ? !isEditMode : false;

  // Clear any lingering validation errors when the form returns to read-only
  // (after Save/Cancel) so "required" messages don't persist in the view state.
  useEffect(() => {
    if (isReadOnly) {
      formik.setTouched({}, false);
    }
  }, [isReadOnly]);

  // The form holds unsaved edits when the address differs from what's saved or a new
  // document has been selected. Used to prompt the user to click Save.
  const isDirty = hasAddressChanged() || newFiles.length > 0;

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty]);

  return (
    <form>
      {isUserLoading && <BaseLoader isLoading={isUserLoading} />}
      {mode !== "USER_SETTINGS" && hasUserData && (
        <div className="flex justify-end gap-2">
          {isEditMode && (
            <BaseButton color="primary" onClick={handleCancel} className="mt-1 py-[5px] px-[38px]">
              Cancel
            </BaseButton>
          )}
          <BaseButton
            color="primary"
            onClick={() => setIsEditMode(true)}
            className={`mt-1 py-[5px] px-[38px] ${
              isEditMode ? "bg-gray-400 text-white cursor-not-allowed" : ""
            }`}
            disabled={isEditMode}
          >
            Edit
          </BaseButton>
        </div>
      )}

      <section className="py-4 px-5">
        <BaseAutoSelect
          placeholder="Select Nationality"
          className="mb-5"
          labelShrink={false}
          required={true}
          isDefaultField={false}
          fullHeight={false}
          fullWidth
          options={[
            {
              label: "Afghan",
              value: "AFGHAN",
            },
            {
              label: "Albanian",
              value: "ALBANIAN",
            },
            {
              label: "Algerian",
              value: "ALGERIAN",
            },
            {
              label: "American",
              value: "AMERICAN",
            },
            {
              label: "Andorran",
              value: "ANDORRAN",
            },
            {
              label: "Angolan",
              value: "ANGOLAN",
            },
            {
              label: "Anguillan",
              value: "ANGUILLAN",
            },
            {
              label: "Citizen of Antigua and Barbuda",
              value: "CITIZEN_OF_ANTIGUA_AND_BARBUDA",
            },
            {
              label: "Argentine",
              value: "ARGENTINE",
            },
            {
              label: "Armenian",
              value: "ARMENIAN",
            },
            {
              label: "Australian",
              value: "AUSTRALIAN",
            },
            {
              label: "Austrian",
              value: "AUSTRIAN",
            },
            {
              label: "Azerbaijani",
              value: "AZERBAIJANI",
            },
            {
              label: "Bahamian",
              value: "BAHAMIAN",
            },
            {
              label: "Bahraini",
              value: "BAHRAINI",
            },
            {
              label: "Bangladeshi",
              value: "BANGLADESHI",
            },
            {
              label: "Barbadian",
              value: "BARBADIAN",
            },
            {
              label: "Belarusian",
              value: "BELARUSIAN",
            },
            {
              label: "Belgian",
              value: "BELGIAN",
            },
            {
              label: "Belizean",
              value: "BELIZEAN",
            },
            {
              label: "Beninese",
              value: "BENINESE",
            },
            {
              label: "Bermudian",
              value: "BERMUDIAN",
            },
            {
              label: "Bhutanese",
              value: "BHUTANESE",
            },
            {
              label: "Bolivian",
              value: "BOLIVIAN",
            },
            {
              label: "Citizen of Bosnia and Herzegovina",
              value: "CITIZEN_OF_BOSNIA_AND_HERZEGOVINA",
            },
            {
              label: "Botswanan",
              value: "BOTSWANAN",
            },
            {
              label: "Brazilian",
              value: "BRAZILIAN",
            },
            {
              label: "British",
              value: "BRITISH",
            },
            {
              label: "British Virgin Islander",
              value: "BRITISH_VIRGIN_ISLANDER",
            },
            {
              label: "Bruneian",
              value: "BRUNEIAN",
            },
            {
              label: "Bulgarian",
              value: "BULGARIAN",
            },
            {
              label: "Burkinan",
              value: "BURKINAN",
            },
            {
              label: "Burmese",
              value: "BURMESE",
            },
            {
              label: "Burundian",
              value: "BURUNDIAN",
            },
            {
              label: "Cambodian",
              value: "CAMBODIAN",
            },
            {
              label: "Cameroonian",
              value: "CAMEROONIAN",
            },
            {
              label: "Canadian",
              value: "CANADIAN",
            },
            {
              label: "Cape Verdean",
              value: "CAPE_VERDEAN",
            },
            {
              label: "Cayman Islander",
              value: "CAYMAN_ISLANDER",
            },
            {
              label: "Central African",
              value: "CENTRAL_AFRICAN",
            },
            {
              label: "Chadian",
              value: "CHADIAN",
            },
            {
              label: "Chilean",
              value: "CHILEAN",
            },
            {
              label: "Chinese",
              value: "CHINESE",
            },
            {
              label: "Colombian",
              value: "COLOMBIAN",
            },
            {
              label: "Comoran",
              value: "COMORAN",
            },
            {
              label: "Congolese (Congo)",
              value: "CONGOLESE_CONGO",
            },
            {
              label: "Congolese (DRC)",
              value: "CONGOLESE_DRC",
            },
            {
              label: "Cook Islander",
              value: "COOK_ISLANDER",
            },
            {
              label: "Costa Rican",
              value: "COSTA_RICAN",
            },
            {
              label: "Croatian",
              value: "CROATIAN",
            },
            {
              label: "Cuban",
              value: "CUBAN",
            },
            {
              label: "Cymraes",
              value: "CYMRAES",
            },
            {
              label: "Cymro",
              value: "CYMRO",
            },
            {
              label: "Cypriot",
              value: "CYPRIOT",
            },
            {
              label: "Czech",
              value: "CZECH",
            },
            {
              label: "Danish",
              value: "DANISH",
            },
            {
              label: "Djiboutian",
              value: "DJIBOUTIAN",
            },
            {
              label: "Dominican",
              value: "DOMINICAN",
            },
            {
              label: "Citizen of the Dominican Republic",
              value: "CITIZEN_OF_THE_DOMINICAN_REPUBLIC",
            },
            {
              label: "Dutch",
              value: "DUTCH",
            },
            {
              label: "East Timorese",
              value: "EAST_TIMORESE",
            },
            {
              label: "Ecuadorean",
              value: "ECUADOREAN",
            },
            {
              label: "Egyptian",
              value: "EGYPTIAN",
            },
            {
              label: "Emirati",
              value: "EMIRATI",
            },
            {
              label: "English",
              value: "ENGLISH",
            },
            {
              label: "Equatorial Guinean",
              value: "EQUATORIAL_GUINEAN",
            },
            {
              label: "Eritrean",
              value: "ERITREAN",
            },
            {
              label: "Estonian",
              value: "ESTONIAN",
            },
            {
              label: "Ethiopian",
              value: "ETHIOPIAN",
            },
            {
              label: "Faroese",
              value: "FAROESE",
            },
            {
              label: "Fijian",
              value: "FIJIAN",
            },
            {
              label: "Filipino",
              value: "FILIPINO",
            },
            {
              label: "Finnish",
              value: "FINNISH",
            },
            {
              label: "French",
              value: "FRENCH",
            },
            {
              label: "Gabonese",
              value: "GABONESE",
            },
            {
              label: "Gambian",
              value: "GAMBIAN",
            },
            {
              label: "Georgian",
              value: "GEORGIAN",
            },
            {
              label: "German",
              value: "GERMAN",
            },
            {
              label: "Ghanaian",
              value: "GHANAIAN",
            },
            {
              label: "Gibraltarian",
              value: "GIBRALTARIAN",
            },
            {
              label: "Greek",
              value: "GREEK",
            },
            {
              label: "Greenlandic",
              value: "GREENLANDIC",
            },
            {
              label: "Grenadian",
              value: "GRENADIAN",
            },
            {
              label: "Guamanian",
              value: "GUAMANIAN",
            },
            {
              label: "Guatemalan",
              value: "GUATEMALAN",
            },
            {
              label: "Citizen of Guinea-Bissau",
              value: "CITIZEN_OF_GUINEA-BISSAU",
            },
            {
              label: "Guinean",
              value: "GUINEAN",
            },
            {
              label: "Guyanese",
              value: "GUYANESE",
            },
            {
              label: "Haitian",
              value: "HAITIAN",
            },
            {
              label: "Honduran",
              value: "HONDURAN",
            },
            {
              label: "Hong Konger",
              value: "HONG_KONGER",
            },
            {
              label: "Hungarian",
              value: "HUNGARIAN",
            },
            {
              label: "Icelandic",
              value: "ICELANDIC",
            },
            {
              label: "Indian",
              value: "INDIAN",
            },
            {
              label: "Indonesian",
              value: "INDONESIAN",
            },
            {
              label: "Iranian",
              value: "IRANIAN",
            },
            {
              label: "Iraqi",
              value: "IRAQI",
            },
            {
              label: "Irish",
              value: "IRISH",
            },
            {
              label: "Israeli",
              value: "ISRAELI",
            },
            {
              label: "Italian",
              value: "ITALIAN",
            },
            {
              label: "Ivorian",
              value: "IVORIAN",
            },
            {
              label: "Jamaican",
              value: "JAMAICAN",
            },
            {
              label: "Japanese",
              value: "JAPANESE",
            },
            {
              label: "Jordanian",
              value: "JORDANIAN",
            },
            {
              label: "Kazakh",
              value: "KAZAKH",
            },
            {
              label: "Kenyan",
              value: "KENYAN",
            },
            {
              label: "Kittitian",
              value: "KITTITIAN",
            },
            {
              label: "Citizen of Kiribati",
              value: "CITIZEN_OF_KIRIBATI",
            },
            {
              label: "Kosovan",
              value: "KOSOVAN",
            },
            {
              label: "Kuwaiti",
              value: "KUWAITI",
            },
            {
              label: "Kyrgyz",
              value: "KYRGYZ",
            },
            {
              label: "Lao",
              value: "LAO",
            },
            {
              label: "Latvian",
              value: "LATVIAN",
            },
            {
              label: "Lebanese",
              value: "LEBANESE",
            },
            {
              label: "Liberian",
              value: "LIBERIAN",
            },
            {
              label: "Libyan",
              value: "LIBYAN",
            },
            {
              label: "Liechtenstein citizen",
              value: "LIECHTENSTEIN_CITIZEN",
            },
            {
              label: "Lithuanian",
              value: "LITHUANIAN",
            },
            {
              label: "Luxembourger",
              value: "LUXEMBOURGER",
            },
            {
              label: "Macanese",
              value: "MACANESE",
            },
            {
              label: "Macedonian",
              value: "MACEDONIAN",
            },
            {
              label: "Malagasy",
              value: "MALAGASY",
            },
            {
              label: "Malawian",
              value: "MALAWIAN",
            },
            {
              label: "Malaysian",
              value: "MALAYSIAN",
            },
            {
              label: "Maldivian",
              value: "MALDIVIAN",
            },
            {
              label: "Malian",
              value: "MALIAN",
            },
            {
              label: "Maltese",
              value: "MALTESE",
            },
            {
              label: "Marshallese",
              value: "MARSHALLESE",
            },
            {
              label: "Martiniquais",
              value: "MARTINIQUAIS",
            },
            {
              label: "Mauritanian",
              value: "MAURITANIAN",
            },
            {
              label: "Mauritian",
              value: "MAURITIAN",
            },
            {
              label: "Mexican",
              value: "MEXICAN",
            },
            {
              label: "Micronesian",
              value: "MICRONESIAN",
            },
            {
              label: "Moldovan",
              value: "MOLDOVAN",
            },
            {
              label: "Monegasque",
              value: "MONEGASQUE",
            },
            {
              label: "Mongolian",
              value: "MONGOLIAN",
            },
            {
              label: "Montenegrin",
              value: "MONTENEGRIN",
            },
            {
              label: "Montserratian",
              value: "MONTSERRATIAN",
            },
            {
              label: "Moroccan",
              value: "MOROCCAN",
            },
            {
              label: "Mosotho",
              value: "MOSOTHO",
            },
            {
              label: "Mozambican",
              value: "MOZAMBICAN",
            },
            {
              label: "Namibian",
              value: "NAMIBIAN",
            },
            {
              label: "Nauruan",
              value: "NAURUAN",
            },
            {
              label: "Nepalese",
              value: "NEPALESE",
            },
            {
              label: "New Zealander",
              value: "NEW_ZEALANDER",
            },
            {
              label: "Nicaraguan",
              value: "NICARAGUAN",
            },
            {
              label: "Nigerian",
              value: "NIGERIAN",
            },
            {
              label: "Nigerien",
              value: "NIGERIEN",
            },
            {
              label: "Niuean",
              value: "NIUEAN",
            },
            {
              label: "North Korean",
              value: "NORTH_KOREAN",
            },
            {
              label: "Northern Irish",
              value: "NORTHERN_IRISH",
            },
            {
              label: "Norwegian",
              value: "NORWEGIAN",
            },
            {
              label: "Omani",
              value: "OMANI",
            },
            {
              label: "Pakistani",
              value: "PAKISTANI",
            },
            {
              label: "Palauan",
              value: "PALAUAN",
            },
            {
              label: "Palestinian",
              value: "PALESTINIAN",
            },
            {
              label: "Panamanian",
              value: "PANAMANIAN",
            },
            {
              label: "Papua New Guinean",
              value: "PAPUA_NEW_GUINEAN",
            },
            {
              label: "Paraguayan",
              value: "PARAGUAYAN",
            },
            {
              label: "Peruvian",
              value: "PERUVIAN",
            },
            {
              label: "Pitcairn Islander",
              value: "PITCAIRN_ISLANDER",
            },
            {
              label: "Polish",
              value: "POLISH",
            },
            {
              label: "Portuguese",
              value: "PORTUGUESE",
            },
            {
              label: "Prydeinig",
              value: "PRYDEINIG",
            },
            {
              label: "Puerto Rican",
              value: "PUERTO_RICAN",
            },
            {
              label: "Qatari",
              value: "QATARI",
            },
            {
              label: "Romanian",
              value: "ROMANIAN",
            },
            {
              label: "Russian",
              value: "RUSSIAN",
            },
            {
              label: "Rwandan",
              value: "RWANDAN",
            },
            {
              label: "Salvadorean",
              value: "SALVADOREAN",
            },
            {
              label: "Sammarinese",
              value: "SAMMARINESE",
            },
            {
              label: "Samoan",
              value: "SAMOAN",
            },
            {
              label: "Sao Tomean",
              value: "SAO_TOMEAN",
            },
            {
              label: "Saudi Arabian",
              value: "SAUDI_ARABIAN",
            },
            {
              label: "Scottish",
              value: "SCOTTISH",
            },
            {
              label: "Senegalese",
              value: "SENEGALESE",
            },
            {
              label: "Serbian",
              value: "SERBIAN",
            },
            {
              label: "Citizen of Seychelles",
              value: "CITIZEN_OF_SEYCHELLES",
            },
            {
              label: "Sierra Leonean",
              value: "SIERRA_LEONEAN",
            },
            {
              label: "Singaporean",
              value: "SINGAPOREAN",
            },
            {
              label: "Slovak",
              value: "SLOVAK",
            },
            {
              label: "Slovenian",
              value: "SLOVENIAN",
            },
            {
              label: "Solomon Islander",
              value: "SOLOMON_ISLANDER",
            },
            {
              label: "Somali",
              value: "SOMALI",
            },
            {
              label: "South African",
              value: "SOUTH_AFRICAN",
            },
            {
              label: "South Korean",
              value: "SOUTH_KOREAN",
            },
            {
              label: "South Sudanese",
              value: "SOUTH_SUDANESE",
            },
            {
              label: "Spanish",
              value: "SPANISH",
            },
            {
              label: "Sri Lankan",
              value: "SRI_LANKAN",
            },
            {
              label: "St Helenian",
              value: "ST_HELENIAN",
            },
            {
              label: "St Lucian",
              value: "ST_LUCIAN",
            },
            {
              label: "Stateless",
              value: "STATELESS",
            },
            {
              label: "Sudanese",
              value: "SUDANESE",
            },
            {
              label: "Surinamese",
              value: "SURINAMESE",
            },
            {
              label: "Swazi",
              value: "SWAZI",
            },
            {
              label: "Swedish",
              value: "SWEDISH",
            },
            {
              label: "Swiss",
              value: "SWISS",
            },
            {
              label: "Syrian",
              value: "SYRIAN",
            },
            {
              label: "Taiwanese",
              value: "TAIWANESE",
            },
            {
              label: "Tajik",
              value: "TAJIK",
            },
            {
              label: "Tanzanian",
              value: "TANZANIAN",
            },
            {
              label: "Thai",
              value: "THAI",
            },
            {
              label: "Togolese",
              value: "TOGOLESE",
            },
            {
              label: "Tongan",
              value: "TONGAN",
            },
            {
              label: "Trinidadian",
              value: "TRINIDADIAN",
            },
            {
              label: "Tristanian",
              value: "TRISTANIAN",
            },
            {
              label: "Tunisian",
              value: "TUNISIAN",
            },
            {
              label: "Turkish",
              value: "TURKISH",
            },
            {
              label: "Turkmen",
              value: "TURKMEN",
            },
            {
              label: "Turks and Caicos Islander",
              value: "TURKS_AND_CAICOS_ISLANDER",
            },
            {
              label: "Tuvaluan",
              value: "TUVALUAN",
            },
            {
              label: "Ugandan",
              value: "UGANDAN",
            },
            {
              label: "Ukrainian",
              value: "UKRAINIAN",
            },
            {
              label: "Uruguayan",
              value: "URUGUAYAN",
            },
            {
              label: "Uzbek",
              value: "UZBEK",
            },
            {
              label: "Vatican citizen",
              value: "VATICAN_CITIZEN",
            },
            {
              label: "Citizen of Vanuatu",
              value: "CITIZEN_OF_VANUATU",
            },
            {
              label: "Venezuelan",
              value: "VENEZUELAN",
            },
            {
              label: "Vietnamese",
              value: "VIETNAMESE",
            },
            {
              label: "Vincentian",
              value: "VINCENTIAN",
            },
            {
              label: "Wallisian",
              value: "WALLISIAN",
            },
            {
              label: "Welsh",
              value: "WELSH",
            },
            {
              label: "Yemeni",
              value: "YEMENI",
            },
            {
              label: "Zambian",
              value: "ZAMBIAN",
            },
            {
              label: "Zimbabwean",
              value: "ZIMBABWEAN",
            },
          ]}
          label="Nationality"
          id="nationality"
          name="nationality"
          defaultValue={formik.values.nationality}
          value={formik.values.nationality}
          onChange={handleChangeNationality}
          onBlur={formik.handleBlur}
          error={formik.touched.nationality && Boolean(formik.errors.nationality)}
          helperText={formik.touched.nationality && formik.errors.nationality}
          disabled={isReadOnly || identityLocked}
        />

        <div>
          <p className="text-primary mb-1">National Id/Driving Lisense/Passport</p>
          {identityLocked && (
            <p className="text-xs text-gray-500 mb-2">
              Your profile is approved, so your nationality and ID documents are locked. Contact
              Tech Domain support if they need correcting.
            </p>
          )}
          <div className="w-full flex flex-wrap items-center gap-3">
            {/* Hidden File Input */}
            <input
              type="file"
              ref={fileInputRef}
              multiple
              onChange={handleFileChange}
              style={{ display: "none" }}
            />

            {/* File Display Section */}
            <div
              style={{
                boxShadow: "0px 0px 6px 0px rgba(0, 0, 0, 0.10)",
                backgroundColor: isReadOnly ? "#f5f5f5" : "white",
              }}
              className="min-h-[40px] flex-1 min-w-0 py-[6.25px] rounded-md flex flex-wrap gap-2 pl-2"
            >
              {selectedFiles?.length > 0 ? (
                selectedFiles?.map((file, index) => (
                  <div
                    key={"identity" + index}
                    className="inline-flex items-center gap-2 bg-[#E9E9EB] rounded-[4px] py-1 pl-4 pr-2 min-w-0 max-w-full"
                  >
                    <a
                      href={file.url}
                      download={file.name}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="cursor-pointer text-blue-500 hover:underline font-300 truncate min-w-0"
                    >
                      {file?.name}
                    </a>
                    <button
                      type="button"
                      onClick={() => handleRemoveSelectedFile(index)}
                      className="flex items-center justify-center h-4 w-4 rounded-full text-gray-600 hover:bg-gray-300 hover:text-gray-800 shrink-0"
                      title="Remove file"
                    >
                      <i className="fa-solid fa-xmark text-xs"></i>
                    </button>
                  </div>
                ))
              ) : (
                <p className="text-gray-400 py-1 ml-2">No file selected</p>
              )}
            </div>
            {(isEditMode || mode === "USER_SETTINGS") && !identityLocked && (
              <BaseButton
                loaderSize={22}
                isLoading={isUploadingIdentityLoading}
                variant="outlined"
                color="primary"
                className="min-w-[124px] py-[7.2px] shrink-0"
                onClick={handleButtonClick}
              >
                Choose File
              </BaseButton>
            )}
          </div>
        </div>
        <div className="flex flex-wrap gap-2 mt-3">
          {identityDocuments?.length > 0 &&
            identityDocuments?.map((file, index) => (
              <div className="flex items-center bg-[#E9E9EB] py-1 px-4 rounded-[4px]">
                <a
                  key={index}
                  href={file.url} // ✅ Ensure this is a valid URL
                  download={file.name} // ✅ Set correct filename for download
                  target="_blank" // ✅ Open in a new tab if needed
                  rel="noopener noreferrer"
                  className={`cursor-default block  ${document?.url && "hover:text-blue-500 cursor-pointer"} font-300`}
                >
                  {file?.filename}
                </a>
                {(mode === "USER_SETTINGS" || isEditMode) && !identityLocked && (
                  <i
                    className="fa-solid fa-xmark ml-2 cursor-pointer hover:text-blue-500"
                    onClick={() => {
                      deleteDigitalAssetMutation(file.id);
                      refetchMe();
                    }}
                  ></i>
                )}
              </div>
            ))}
        </div>
        <h6 className="font-normal !text-dark mt-3">Your Home Address</h6>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 lg:gap-x-12 lg:gap-y-4 mt-3">
          {(isEditMode || mode === "USER_SETTINGS") && (
            <div className="md:col-span-1 col-span-2">
              <SearchLocation onPlaceSelected={handlePlaceSelected} />
            </div>
          )}
          <div className="md:col-span-1 col-span-2">
            <BaseTextField
              labelShrink={false}
              label="Address"
              optional={false}
              isDefaultField={false}
              required
              fullWidth
              id="addressLine1"
              name="addressLine1"
              value={formik.values.addressLine1}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.addressLine1 && Boolean(formik.errors.addressLine1)}
              helperText={formik.touched.addressLine1 && formik.errors.addressLine1}
              disabled={isReadOnly}
            />
          </div>
          {/* <BaseTextField
            labelShrink={false}
            label="Address Line 2"
            optional={true}
            isDefaultField={false}
            id="addressLine2"
            name="addressLine2"
            value={formik.values.addressLine2}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              formik.touched.addressLine2 && Boolean(formik.errors.addressLine2)
            }
            helperText={
              formik.touched.addressLine2 && formik.errors.addressLine2
            }
          /> */}
        </div>
        <div className="md:col-span-1 col-span-2">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-x-6 gap-y-3 lg:gap-x-12 mt-3">
            <div className="md:col-span-1 col-span-4">
              <BaseTextField
                optional={false}
                labelShrink={false}
                label="Country"
                isDefaultField={false}
                id="country"
                name="country"
                value={formik.values.country}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.country && Boolean(formik.errors.country)}
                helperText={formik.touched.country && formik.errors.country}
                disabled={isReadOnly}
              />
            </div>
            <div className="md:col-span-1 col-span-4">
              <BaseTextField
                optional={false}
                labelShrink={false}
                label="City"
                isDefaultField={false}
                id="city"
                name="city"
                value={formik.values.city}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.city && Boolean(formik.errors.city)}
                helperText={formik.touched.city && formik.errors.city}
                disabled={isReadOnly}
              />
            </div>
            <div className="md:col-span-1 col-span-4">
              <BaseTextField
                // disabled={formik.values.state !== "" ? true : false}
                labelShrink={false}
                label="State"
                optional={false}
                isDefaultField={false}
                id="state"
                name="state"
                value={formik.values.state}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.state && Boolean(formik.errors.state)}
                helperText={formik.touched.state && formik.errors.state}
                disabled={isReadOnly}
              />
            </div>
            <div className="md:col-span-1 col-span-4">
              <BaseTextField
                labelShrink={false}
                label="Zip Code"
                optional={false}
                isDefaultField={false}
                id="zipCode"
                name="zipCode"
                value={formik.values.zipCode}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.zipCode && Boolean(formik.errors.zipCode)}
                helperText={formik.touched.zipCode && formik.errors.zipCode}
                disabled={isReadOnly}
              />
            </div>
          </div>
        </div>

        {!isReadOnly && (
          <div className="flex flex-col md:items-end items-center gap-1 mt-5">
            {isDirty && (
              <p className="text-amber-600 text-sm font-normal">
                You have unsaved changes — click Save to apply.
              </p>
            )}
            <BaseButton
              loaderSize="22px"
              isLoading={isUserAddressLoading || isUploadingIdentityLoading}
              color="primary"
              className="py-[5px] px-[38px]"
              // type="submit"
              disabled={
                !isDirty ||
                (hasAddressChanged() &&
                  (!formik.values.addressLine1 ||
                    !formik.values.country ||
                    !formik.values.city ||
                    !formik.values.state))
              }
              onClick={handleSubmit}
            >
              Save
            </BaseButton>
          </div>
        )}
      </section>
      {showBillingRatesAlert && (
        <div className="fixed top-0 left-0 w-full h-full flex items-center justify-center bg-black bg-opacity-30 z-50">
          <div className="bg-white p-6 rounded shadow-lg w-[500px] text-center">
            <h6 className="font-normal mb-4 text-red-600">Country updated!</h6>
            <p className="mb-4">
              Your billing rates have been reset. Please update them before submitting.
            </p>
            <BaseButton
              color="primary"
              className="py-[5px] px-[38px]"
              onClick={() => setShowBillingRatesAlert(false)}
            >
              OK
            </BaseButton>
          </div>
        </div>
      )}
    </form>
  );
};

export default VerifyIdentity;
